/**
 * User Preferences Page
 *
 * Page for managing user preferences including timezone, theme, and language.
 * Implements ADR-007 Timezone Strategy for user-level timezone preferences.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Loader2, CheckCircle, AlertTriangle, Clock, Globe } from 'lucide-react';
import { Card } from '@/components/ui';
import { cn } from '@/lib/utils';
import apiClient from '@/lib/api-client';
import { TimezoneSelect } from '@vanguard-atlas/shared';
import { formatDateTime, setUserTimezone } from '@vanguard-atlas/shared';

// =============================================================================
// Types
// =============================================================================

interface UserPreferences {
  timezone: string | null;
  date_format: string | null;
  time_format: string | null;
  theme: string;
  language: string;
}

interface CurrentUserInfo {
  id: string;
  email: string;
  name: string;
  tenant_id: string;
  roles: string[];
  timezone: string;
  date_format: string;
  time_format: string;
  preferences: UserPreferences;
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Fetch current user from API.
 * The /auth/me endpoint returns UserInfoResponse directly (no wrapper).
 */
async function fetchCurrentUser(): Promise<CurrentUserInfo> {
  const response = await apiClient.get<CurrentUserInfo>('/api/v1/auth/me');
  // Backend returns the object directly, not wrapped in {data: ...}
  return response.data;
}

/**
 * Update user preferences via API.
 * Response format: UserPreferences (direct, no wrapper for PATCH)
 */
async function updatePreferences(preferences: Partial<UserPreferences>): Promise<UserPreferences> {
  const response = await apiClient.patch<UserPreferences>('/api/v1/auth/me/preferences', preferences);
  return response.data;
}

// =============================================================================
// Time Format Options
// =============================================================================

const TIME_FORMAT_OPTIONS = [
  { value: '12h', label: '12-hour (3:30 PM)' },
  { value: '24h', label: '24-hour (15:30)' },
];

const THEME_OPTIONS = [
  { value: 'system', label: 'System (Auto)' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

// =============================================================================
// Preferences Page Component
// =============================================================================

export function PreferencesPage() {
  const queryClient = useQueryClient();
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Fetch current user info
  const { data: currentUser, isLoading, error } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });

  // Update preferences mutation
  const updateMutation = useMutation({
    mutationFn: updatePreferences,
    onSuccess: (data) => {
      // Update the query cache
      queryClient.setQueryData(['currentUser'], (old: CurrentUserInfo | undefined) => {
        if (!old) return old;
        return {
          ...old,
          timezone: data.timezone || old.timezone,
          preferences: data,
        };
      });
      
      // Update localStorage for datetime utilities
      if (data.timezone) {
        setUserTimezone(data.timezone);
      }
      
      // Show success message briefly
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    },
  });

  // Sync timezone to localStorage on load
  useEffect(() => {
    if (currentUser?.timezone) {
      setUserTimezone(currentUser.timezone);
    }
  }, [currentUser?.timezone]);

  // Handle timezone change
  const handleTimezoneChange = (timezone: string) => {
    updateMutation.mutate({ timezone });
  };

  // Handle time format change
  const handleTimeFormatChange = (time_format: string) => {
    updateMutation.mutate({ time_format });
  };

  // Handle theme change
  const handleThemeChange = (theme: string) => {
    updateMutation.mutate({ theme });
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-6">
        <div className="max-w-2xl mx-auto p-4 bg-red-50 dark:bg-red-900/20 rounded-lg text-red-700 dark:text-red-300 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          Failed to load user preferences
        </div>
      </div>
    );
  }

  const effectiveTimezone = currentUser?.timezone || 'UTC';
  const userPrefs: UserPreferences = currentUser?.preferences || { 
    timezone: null, 
    date_format: null, 
    time_format: null, 
    theme: 'system', 
    language: 'en' 
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/settings"
          className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Settings
        </Link>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Preferences
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Customize your display and localization settings
        </p>
      </div>

      {/* Success notification */}
      {saveSuccess && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg text-green-700 dark:text-green-300 flex items-center gap-2">
          <CheckCircle className="w-5 h-5" />
          Preferences saved successfully
        </div>
      )}

      {/* Error notification */}
      {updateMutation.isError && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg text-red-700 dark:text-red-300 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          Failed to save preferences. Please try again.
        </div>
      )}

      {/* Date & Time Card */}
      <Card className="p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
            <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              Date & Time
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Configure how dates and times are displayed
            </p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Timezone */}
          <div>
            <label
              htmlFor="timezone"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Timezone
            </label>
            <TimezoneSelect
              id="timezone"
              value={effectiveTimezone}
              onChange={handleTimezoneChange}
              disabled={updateMutation.isPending}
            />
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              All dates and times will be displayed in this timezone.
              {userPrefs.timezone ? (
                <span className="ml-1 text-blue-600 dark:text-blue-400">
                  (Custom preference)
                </span>
              ) : (
                <span className="ml-1">
                  (Using tenant default)
                </span>
              )}
            </p>
            
            {/* Current time preview */}
            <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Current time in {effectiveTimezone}:
              </p>
              <p className="text-lg font-medium text-gray-900 dark:text-gray-100">
                {formatDateTime(new Date(), 'PPpp', effectiveTimezone)}
              </p>
            </div>
          </div>

          {/* Time format */}
          <div>
            <label
              htmlFor="time-format"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Time Format
            </label>
            <select
              id="time-format"
              value={userPrefs.time_format || currentUser?.time_format || '24h'}
              onChange={(e) => handleTimeFormatChange(e.target.value)}
              disabled={updateMutation.isPending}
              className={cn(
                'w-full px-3 py-2',
                'bg-white dark:bg-gray-800',
                'border border-gray-300 dark:border-gray-600',
                'rounded-md shadow-sm',
                'text-gray-900 dark:text-gray-100',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                'disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:cursor-not-allowed'
              )}
            >
              {TIME_FORMAT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Appearance Card */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
            <Globe className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              Appearance
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Customize the look and feel of the application
            </p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Theme */}
          <div>
            <label
              htmlFor="theme"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Theme
            </label>
            <select
              id="theme"
              value={userPrefs.theme || 'system'}
              onChange={(e) => handleThemeChange(e.target.value)}
              disabled={updateMutation.isPending}
              className={cn(
                'w-full px-3 py-2',
                'bg-white dark:bg-gray-800',
                'border border-gray-300 dark:border-gray-600',
                'rounded-md shadow-sm',
                'text-gray-900 dark:text-gray-100',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                'disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:cursor-not-allowed'
              )}
            >
              {THEME_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              Choose your preferred color theme for the interface.
            </p>
          </div>
        </div>
      </Card>

      {/* Saving indicator */}
      {updateMutation.isPending && (
        <div className="fixed bottom-6 right-6 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          Saving...
        </div>
      )}
    </div>
  );
}

export default PreferencesPage;
